import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const MessagePreview = styled.div<{ $expanded: boolean; $overflowing: boolean }>`
  position: relative;
  max-height: ${({ $expanded }) => ($expanded ? 'none' : '14rem')};
  overflow: hidden;
  ${({ $expanded, $overflowing }) => $overflowing && !$expanded && `
    mask-image: linear-gradient(to bottom, #000 calc(100% - 2.2rem), transparent 100%);
    -webkit-mask-image: linear-gradient(to bottom, #000 calc(100% - 2.2rem), transparent 100%);
  `}
`;

const ToggleButton = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-top: 7px;
  padding: 3px 0;
  opacity: 0.78;
  &:hover { opacity: 1; text-decoration: underline; }
  &:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; border-radius: 3px; }
  svg { font-size: 1rem; }
`;

const CollapsibleUserMessage: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  const measure = useCallback(() => {
    const element = contentRef.current;
    if (!element || expanded) return;
    setOverflowing(element.scrollHeight > element.clientHeight + 1);
  }, [expanded]);

  useLayoutEffect(() => {
    measure();
    if (typeof ResizeObserver === 'undefined' || !contentRef.current) return;
    const observer = new ResizeObserver(measure);
    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [children, measure]);

  return (
    <>
      <MessagePreview ref={contentRef} $expanded={expanded} $overflowing={overflowing}
        data-testid="collapsible-user-message-content"
        data-expanded={String(expanded)}
        data-overflowing={String(overflowing)}>
        {children}
      </MessagePreview>
      {overflowing && (
        <ToggleButton type="button" aria-label={expanded ? 'Recolher mensagem' : 'Expandir mensagem'}
          onClick={() => setExpanded((value) => !value)}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          {expanded ? 'Recolher' : 'Expandir'}
        </ToggleButton>
      )}
    </>
  );
};

export default CollapsibleUserMessage;
